export interface FieldCampusInputOptions {
  readonly minX: number;
  readonly maxX: number;
  readonly baselineY: number;
  readonly speed?: number;
  readonly depth?: number;
  readonly onPositionChanged?: (x: number) => void;
}

/** Pointer/touch and keyboard exploration input with no visible programmer art. */
export class FieldCampusInputController {
  private readonly pointerZone: Phaser.GameObjects.Zone;
  private readonly cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private readonly leftKey?: Phaser.Input.Keyboard.Key;
  private readonly rightKey?: Phaser.Input.Keyboard.Key;
  private readonly speed: number;
  private targetX: number | null = null;
  private enabled = false;
  private baselineY: number;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly character: Phaser.GameObjects.Image,
    private readonly options: FieldCampusInputOptions,
  ) {
    this.speed = options.speed ?? 190;
    this.baselineY = options.baselineY;
    this.cursors = scene.input.keyboard?.createCursorKeys();
    this.leftKey = scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.rightKey = scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.pointerZone = scene.add.zone(
      (options.minX + options.maxX) * 0.5,
      this.baselineY - 34,
      options.maxX - options.minX,
      210,
    ).setDepth(options.depth ?? 45).setInteractive({ useHandCursor: true });
    this.pointerZone.input!.enabled = false;
    this.pointerZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      this.targetX = Phaser.Math.Clamp(pointer.worldX, options.minX, options.maxX);
    });
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
  }

  enable() {
    this.enabled = true;
    this.pointerZone.input!.enabled = true;
  }

  disable() {
    this.enabled = false;
    this.targetX = null;
    this.pointerZone.input!.enabled = false;
  }

  setBaselineY(baselineY: number) {
    this.baselineY = baselineY;
    this.pointerZone.setY(baselineY - 34);
    this.character.setY(baselineY);
  }

  private update(_time: number, delta: number) {
    if (!this.enabled) return;
    const left = this.cursors?.left.isDown || this.leftKey?.isDown;
    const right = this.cursors?.right.isDown || this.rightKey?.isDown;
    const direction = (right ? 1 : 0) - (left ? 1 : 0);
    const step = this.speed * Math.min(delta, 32) / 1000;

    if (direction !== 0) {
      this.targetX = null;
      this.character.x = Phaser.Math.Clamp(
        this.character.x + direction * step,
        this.options.minX,
        this.options.maxX,
      );
    } else if (this.targetX !== null) {
      const distance = this.targetX - this.character.x;
      if (Math.abs(distance) <= step) {
        this.character.x = this.targetX;
        this.targetX = null;
      } else {
        this.character.x += Math.sign(distance) * step;
      }
    }

    this.character.y = this.baselineY;
    this.options.onPositionChanged?.(this.character.x);
  }

  destroy() {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.pointerZone.destroy();
  }
}
